import React, {
	useRef,
	useEffect,
	useState,
	useMemo,
	useImperativeHandle,
	forwardRef,
} from "react";
import {
	Stage,
	Layer,
	Rect,
	Line,
	Text,
	Image as KImage,
	Group,
	Circle,
} from "react-konva";

const CANVAS_W = 1080;
const CANVAS_H = 1920;
const STICKER_W = 200;
const DEFAULT_ACCENT = "#6366f1";

/* ── helpers ── */

function getDefaultPosition(index) {
	const startY = 560;
	const gap = 240;
	const y = Math.min(startY + index * gap, 1680);
	const x = CANVAS_W / 2 + (index % 2 === 0 ? -140 : 140);
	return { x, y };
}

function measureText(text, fontSize, fontStyle = "bold", fontFamily = "Poppins") {
	if (typeof document === "undefined") return text.length * fontSize * 0.55;
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");
	const weight = fontStyle === "600" ? "600" : "700";
	ctx.font = `${weight} ${fontSize}px "${fontFamily}"`;
	return ctx.measureText(text).width;
}

function useFontReady() {
	const [ready, setReady] = useState(
		typeof document !== "undefined" && document.fonts?.status === "loaded"
	);
	useEffect(() => {
		if (typeof document === "undefined" || !document.fonts) {
			setReady(true);
			return;
		}
		document.fonts.load('700 52px "Poppins"').finally(() => {
			document.fonts.ready.then(() => setReady(true));
		});
	}, []);
	return ready;
}

function wrapLines(text, fontSize, maxWidth, fontStyle = "bold") {
	const words = String(text).split(/\s+/).filter(Boolean);
	if (words.length === 0) return [""];
	const lines = [];
	let current = words[0];
	for (let i = 1; i < words.length; i++) {
		const next = `${current} ${words[i]}`;
		if (measureText(next, fontSize, fontStyle) <= maxWidth) {
			current = next;
		} else {
			lines.push(current);
			current = words[i];
		}
	}
	lines.push(current);
	return lines;
}

/* ── Soft place-name label ── */

function PillLabel({
	text,
	x,
	y,
	fontSize = 14,
	maxWidth = 400,
	paddingX = 18,
	paddingY = 8,
	fill,
	accentColor,
	align = "center",
}) {
	const textFill = fill || accentColor || DEFAULT_ACCENT;
	const lines = useMemo(
		() => wrapLines(text, fontSize, maxWidth - paddingX * 2),
		[text, fontSize, maxWidth, paddingX]
	);
	const lineHeight = fontSize * 1.3;
	const textW = Math.max(
		...lines.map((l) => measureText(l, fontSize)),
		20
	);
	const boxW = Math.min(maxWidth, Math.ceil(textW) + paddingX * 2);
	const boxH = lines.length * lineHeight + paddingY * 2;
	const corner = Math.min(boxH / 2, 24);
	const left = align === "center" ? x - boxW / 2 : x;

	return (
		<Group x={left} y={y}>
			<Rect
				width={boxW}
				height={boxH}
				fill="rgba(255,255,255,0.92)"
				cornerRadius={corner}
				shadowColor="rgba(0,0,0,0.1)"
				shadowBlur={10}
				shadowOffsetY={3}
				shadowOpacity={1}
			/>
			{lines.map((line, i) => (
				<Text
					key={`${i}-${line}`}
					text={line}
					x={0}
					y={paddingY + i * lineHeight}
					width={boxW}
					height={lineHeight}
					align="center"
					verticalAlign="middle"
					fontFamily="Poppins"
					fontStyle="bold"
					fontSize={fontSize}
					fill={textFill}
				/>
			))}
		</Group>
	);
}

/* ── Title + day header: text always canvas-centered, pill fits around it ── */

function HeaderTitle({ text, x, y, fontSize, maxWidth, fill }) {
	const fontsReady = useFontReady();
	const textRef = useRef(null);
	const band = maxWidth;
	const lines = useMemo(
		() => wrapLines(text, fontSize, maxWidth - 80),
		[text, fontSize, maxWidth, fontsReady]
	);
	const lineHeight = fontSize * 1.05;
	const padX = 48;
	const padY = 18;
	const [pillW, setPillW] = useState(Math.min(maxWidth, 320));

	useEffect(() => {
		const node = textRef.current;
		if (!node) return;
		const next = Math.min(band, Math.ceil(node.getTextWidth()) + padX * 2);
		if (next > 80) setPillW(next);
	}, [text, fontSize, fontsReady, lines, band]);

	const boxH = lines.length * lineHeight + padY * 2;

	return (
		<Group x={x} y={y}>
			<Rect
				x={-pillW / 2}
				y={0}
				width={pillW}
				height={boxH}
				fill="rgba(255,255,255,0.92)"
				cornerRadius={Math.min(boxH / 2, 28)}
				shadowColor="rgba(26,26,46,0.16)"
				shadowBlur={22}
				shadowOffsetY={6}
				shadowOpacity={1}
			/>
			{lines.map((line, i) => (
				<Text
					key={`${i}-${line}`}
					ref={i === 0 ? textRef : undefined}
					text={line}
					x={-band / 2}
					y={(boxH - lines.length * lineHeight) / 2 + i * lineHeight + fontSize * 0.06}
					width={band}
					height={lineHeight}
					align="center"
					verticalAlign="middle"
					fontFamily="Poppins"
					fontStyle="bold"
					fontSize={fontSize}
					fill={fill}
					listening={false}
				/>
			))}
		</Group>
	);
}

function HeaderDay({ text, x, y, fontSize, accentColor }) {
	const fontsReady = useFontReady();
	const textRef = useRef(null);
	const band = 700;
	const padX = 32;
	const padY = 12;
	const boxH = fontSize + padY * 2;
	const [pillW, setPillW] = useState(160);

	useEffect(() => {
		const node = textRef.current;
		if (!node) return;
		const next = Math.ceil(node.getTextWidth()) + padX * 2;
		if (next > 60) setPillW(next);
	}, [text, fontSize, fontsReady]);

	return (
		<Group x={x} y={y}>
			<Rect
				x={-pillW / 2}
				y={0}
				width={pillW}
				height={boxH}
				fill={accentColor}
				cornerRadius={boxH / 2}
				shadowColor={`${accentColor}66`}
				shadowBlur={16}
				shadowOffsetY={6}
				shadowOpacity={1}
			/>
			<Text
				ref={textRef}
				text={text}
				x={-band / 2}
				y={0}
				width={band}
				height={boxH}
				align="center"
				verticalAlign="middle"
				fontFamily="Poppins"
				fontStyle="bold"
				fontSize={fontSize}
				fill="#FFFFFF"
				listening={false}
			/>
		</Group>
	);
}

/* ── image loader hook ── */

function useLoadedImage(src) {
	const [img, setImg] = useState(null);
	useEffect(() => {
		if (!src) {
			setImg(null);
			return;
		}
		const image = new window.Image();
		image.crossOrigin = "anonymous";
		image.onload = () => setImg(image);
		image.onerror = () => setImg(null);
		image.src = src;
	}, [src]);
	return img;
}

/* ── Single sticker on canvas ── */

function OrderBadge({ order, x, y, accentColor }) {
	const accent = accentColor || DEFAULT_ACCENT;
	const label = String(order);
	const r = 28;
	return (
		<Group x={x} y={y}>
			<Circle radius={r + 4} fill="#FFFFFF" shadowColor="rgba(0,0,0,0.12)" shadowBlur={10} shadowOffsetY={3} />
			<Circle
				radius={r}
				fill={accent}
			/>
			<Text
				text={label}
				width={r * 2}
				height={r * 2}
				offsetX={r}
				offsetY={r}
				align="center"
				verticalAlign="middle"
				fontFamily="Poppins"
				fontStyle="bold"
				fontSize={order > 9 ? 22 : 26}
				fill="#FFFFFF"
			/>
		</Group>
	);
}

function PlaceSticker({ stop, order, placeData, customType, bg, onDragEnd, accentColor }) {
	const img = useLoadedImage(placeData?.image || customType?.image || null);
	const emoji = placeData?.emoji || customType?.emoji || "📍";
	const name =
		stop.type === "place"
			? placeData?.name || stop.placeName || ""
			: stop.customName || customType?.name || "";

	const meta = [stop.time, stop.note].filter(Boolean).join(" · ");
	const stickerH = img ? STICKER_W * (img.height / img.width) : STICKER_W;
	const labelY = (img ? stickerH / 2 : 64) + 16;
	const halfW = img ? STICKER_W / 2 : 64;
	const halfH = img ? stickerH / 2 : 64;

	return (
		<Group
			x={stop.x}
			y={stop.y}
			draggable
			onDragEnd={(e) => onDragEnd(stop.id, e.target.x(), e.target.y())}
		>
			{img ? (
				<KImage
					image={img}
					width={STICKER_W}
					height={stickerH}
					offsetX={STICKER_W / 2}
					offsetY={stickerH / 2}
					shadowColor="rgba(0,0,0,0.18)"
					shadowBlur={16}
					shadowOffsetY={6}
				/>
			) : (
				<>
					<Circle
						radius={64}
						fill={bg.stopBg}
						stroke={bg.stopBorder}
						strokeWidth={3}
					/>
					<Text text={emoji} fontSize={48} offsetX={24} offsetY={24} />
				</>
			)}

			{/* Orden de visita */}
			<OrderBadge order={order} x={-halfW + 4} y={-halfH + 4} accentColor={accentColor} />

			{name && (
				<PillLabel
					text={name}
					x={0}
					y={labelY}
					fontSize={22}
					maxWidth={300}
					paddingX={20}
					paddingY={10}
					accentColor={accentColor}
				/>
			)}

			{meta && (
				<PillLabel
					text={meta}
					x={0}
					y={labelY + 50}
					fontSize={18}
					maxWidth={300}
					paddingX={16}
					paddingY={8}
					fill={`${accentColor || DEFAULT_ACCENT}aa`}
				/>
			)}
		</Group>
	);
}

/* ── Main canvas component ── */

const RouteCanvas = forwardRef(function RouteCanvas(
	{ routeState, cityPack, onStopMove },
	ref
) {
	const containerRef = useRef(null);
	const stageRef = useRef(null);
	const [containerWidth, setContainerWidth] = useState(340);

	useImperativeHandle(ref, () => ({
		toDataURL: (opts) => {
			if (!stageRef.current) return null;
			const exportRatio = CANVAS_W / containerWidth;
			return stageRef.current.toDataURL({
				...opts,
				pixelRatio: (opts?.pixelRatio || 1) * exportRatio,
			});
		},
		getStage: () => stageRef.current,
	}));

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const ro = new ResizeObserver((entries) => {
			const w = entries[0]?.contentRect.width;
			if (w && w > 0) setContainerWidth(w);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const scale = containerWidth / CANVAS_W;
	const bg =
		cityPack.backgrounds.find((b) => b.id === routeState.backgroundId) ||
		cityPack.backgrounds[0];
	const bgImage = useLoadedImage(bg.image || null);
	const accentColor = routeState.accentColor || DEFAULT_ACCENT;

	const bgCover = useMemo(() => {
		if (!bgImage) return null;
		const imgW = bgImage.width;
		const imgH = bgImage.height;
		const scale = Math.max(CANVAS_W / imgW, CANVAS_H / imgH);
		const w = imgW * scale;
		const h = imgH * scale;
		return { w, h, x: (CANVAS_W - w) / 2, y: (CANVAS_H - h) / 2 };
	}, [bgImage]);

	const linePoints = useMemo(
		() => routeState.stops.flatMap((s) => [s.x, s.y]),
		[routeState.stops]
	);

	const titleText = routeState.title || "Mi viaje";
	const titleFontSize = 52;
	const titleMaxW = CANVAS_W - 140;
	const titleLines = wrapLines(titleText, titleFontSize, titleMaxW - 64);
	const titleBoxH = titleLines.length * titleFontSize * 1.15 + 44;
	const titleY = 88;
	const dayY = titleY + titleBoxH + 22;

	const handleDragEnd = (stopId, newX, newY) => {
		const clampedX = Math.max(80, Math.min(CANVAS_W - 80, newX));
		const clampedY = Math.max(120, Math.min(CANVAS_H - 120, newY));
		onStopMove(stopId, clampedX, clampedY);
	};

	return (
		<div ref={containerRef} className="rb-canvas-container">
			<Stage
				ref={stageRef}
				width={containerWidth}
				height={containerWidth * (CANVAS_H / CANVAS_W)}
				scaleX={scale}
				scaleY={scale}
			>
				<Layer>
					{/* Background: image or gradient */}
					{bgImage && bgCover ? (
						<KImage
							image={bgImage}
							x={bgCover.x}
							y={bgCover.y}
							width={bgCover.w}
							height={bgCover.h}
						/>
					) : (
						<Rect
							width={CANVAS_W}
							height={CANVAS_H}
							fillLinearGradientStartPoint={{ x: CANVAS_W * 0.1, y: 0 }}
							fillLinearGradientEndPoint={{
								x: CANVAS_W * 0.9,
								y: CANVAS_H,
							}}
							fillLinearGradientColorStops={bg.colorStops}
						/>
					)}

					{/* Title */}
					<HeaderTitle
						text={titleText}
						x={CANVAS_W / 2}
						y={titleY}
						fontSize={titleFontSize}
						maxWidth={titleMaxW}
						fill={accentColor}
					/>

					{/* Day — pastilla accent */}
					{routeState.dayLabel && (
						<HeaderDay
							text={routeState.dayLabel}
							x={CANVAS_W / 2}
							y={dayY}
							fontSize={26}
							accentColor={accentColor}
						/>
					)}

					{/* Connecting route line */}
					{linePoints.length >= 4 && (
						<Line
							points={linePoints}
							stroke={accentColor}
							strokeWidth={5}
							dash={[18, 14]}
							opacity={0.5}
							tension={0.35}
							lineCap="round"
							lineJoin="round"
						/>
					)}

					{/* Place stickers */}
					{routeState.stops.map((stop, idx) => {
						const placeData =
							stop.type === "place"
								? cityPack.places.find((p) => p.id === stop.placeId)
								: null;
						const customType =
							stop.type === "custom"
								? cityPack.customBlockTypes.find(
										(t) => t.id === stop.customType
									)
								: null;
						return (
							<PlaceSticker
								key={stop.id}
								stop={stop}
								order={idx + 1}
								placeData={placeData}
								customType={customType}
								bg={bg}
								onDragEnd={handleDragEnd}
								accentColor={accentColor}
							/>
						);
					})}

					{/* Empty state */}
					{routeState.stops.length === 0 && (
						<Text
							text="Añade lugares desde el panel ✨"
							x={0}
							y={CANVAS_H / 2 - 20}
							width={CANVAS_W}
							align="center"
							fontFamily="Nunito Sans, sans-serif"
							fontSize={30}
							fill={bg.textSecondary}
							opacity={0.6}
						/>
					)}

					{/* Watermark */}
					<Text
						text="Creado con Viajes Molones ✨"
						x={0}
						y={CANVAS_H - 72}
						width={CANVAS_W}
						align="center"
						fontFamily="Poppins"
						fontSize={18}
						fill="rgba(44,62,80,0.78)"
						shadowColor="rgba(255,255,255,0.95)"
						shadowBlur={10}
						shadowOffsetY={0}
						shadowOpacity={1}
					/>
				</Layer>
			</Stage>
		</div>
	);
});

export { CANVAS_W, CANVAS_H, getDefaultPosition };
export default RouteCanvas;
